// Fallback rendering when validation fails

import { PaperDimensions } from './PaperCanvas';

export interface FallbackRenderOptions {
  fontFamily?: string;
  fontSize?: number;
  lineHeight?: number;
  textColor?: string;
  backgroundColor?: string;
  showPlaceholder?: boolean;
  placeholderText?: string;
}

export class ValidationFallback {
  private static readonly DEFAULT_OPTIONS: Required<FallbackRenderOptions> = {
    fontFamily: 'Times New Roman',
    fontSize: 12,
    lineHeight: 1.4,
    textColor: '#000000',
    backgroundColor: '#ffffff',
    showPlaceholder: true,
    placeholderText: 'Waiting for valid backend layout...'
  };

  /**
   * Fallback to local rendering when backend plan validation fails
   */
  static renderFallback(
    ctx: CanvasRenderingContext2D,
    text: string,
    paperDimensions: PaperDimensions,
    options: FallbackRenderOptions = {}
  ): void {
    const opts = { ...this.DEFAULT_OPTIONS, ...options };
    
    console.warn('🔄 [ValidationFallback] Rendering with local fallback due to validation failure');

    // Clear and set background
    ctx.fillStyle = opts.backgroundColor;
    ctx.fillRect(0, 0, paperDimensions.paperWidth, paperDimensions.paperHeight);

    // Draw paper border
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, paperDimensions.paperWidth, paperDimensions.paperHeight);

    // Draw margin guides
    this.drawMarginGuides(ctx, paperDimensions);

    // Configure text rendering
    ctx.font = `${opts.fontSize}px "${opts.fontFamily}"`;
    ctx.fillStyle = opts.textColor;
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    
    const margin = paperDimensions.margins.left;
    const lineHeight = opts.fontSize * opts.lineHeight;
    const maxWidth = paperDimensions.contentWidth;
    const maxHeight = paperDimensions.contentHeight;
    
    if (!text || text.length === 0) {
      if (opts.showPlaceholder) {
        this.renderPlaceholder(ctx, opts.placeholderText, paperDimensions, opts);
      }
      return;
    }

    // Simple word wrapping with line height
    const words = text.split(/\s+/);
    let currentLine = '';
    let y = paperDimensions.margins.top + lineHeight;

    for (const word of words) {
      const testLine = currentLine + (currentLine ? ' ' : '') + word;
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && currentLine) {
        // Render current line and start new one
        ctx.fillText(currentLine, margin, y);
        currentLine = word;
        y += lineHeight;
        
        // Stop if we exceed page height
        if (y > paperDimensions.margins.top + maxHeight) {
          // Show truncation indicator
          ctx.fillStyle = '#666666';
          ctx.font = `italic ${opts.fontSize}px "${opts.fontFamily}"`;
          ctx.fillText('...', margin, y - lineHeight + opts.fontSize);
          break;
        }
      } else {
        currentLine = testLine;
      }
    }
    
    // Render final line
    if (currentLine && y <= paperDimensions.margins.top + maxHeight) {
      ctx.fillText(currentLine, margin, y);
    }

    // Show fallback indicator
    this.renderFallbackIndicator(ctx, paperDimensions, opts);
  }

  /**
   * Render placeholder text when no content is available
   */
  private static renderPlaceholder(
    ctx: CanvasRenderingContext2D,
    placeholderText: string,
    paperDimensions: PaperDimensions,
    options: Required<FallbackRenderOptions>
  ): void {
    ctx.save();
    
    ctx.fillStyle = '#999999';
    ctx.font = `italic ${options.fontSize}px "${options.fontFamily}"`;
    
    const x = paperDimensions.margins.left;
    const y = paperDimensions.margins.top + options.fontSize * options.lineHeight;
    
    ctx.fillText(placeholderText, x, y);
    
    ctx.restore();
  }

  /**
   * Draw margin guides to show content boundaries
   */
  private static drawMarginGuides(
    ctx: CanvasRenderingContext2D,
    paperDimensions: PaperDimensions
  ): void {
    ctx.save();
    
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);

    const { margins, paperWidth, paperHeight } = paperDimensions;

    // Top margin
    ctx.beginPath();
    ctx.moveTo(0, margins.top);
    ctx.lineTo(paperWidth, margins.top);
    ctx.stroke();

    // Bottom margin
    ctx.beginPath();
    ctx.moveTo(0, paperHeight - margins.bottom);
    ctx.lineTo(paperWidth, paperHeight - margins.bottom);
    ctx.stroke();

    // Left margin
    ctx.beginPath();
    ctx.moveTo(margins.left, 0);
    ctx.lineTo(margins.left, paperHeight);
    ctx.stroke();

    // Right margin
    ctx.beginPath();
    ctx.moveTo(paperWidth - margins.right, 0);
    ctx.lineTo(paperWidth - margins.right, paperHeight);
    ctx.stroke();

    ctx.restore();
  }

  /**
   * Render fallback mode indicator
   */
  private static renderFallbackIndicator(
    ctx: CanvasRenderingContext2D,
    paperDimensions: PaperDimensions,
    options: Required<FallbackRenderOptions>
  ): void {
    ctx.save();
    
    // Semi-transparent overlay at bottom
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    const indicatorHeight = 24;
    const y = paperDimensions.paperHeight - indicatorHeight;
    ctx.fillRect(0, y, paperDimensions.paperWidth, indicatorHeight);

    // Border
    ctx.strokeStyle = '#fbbf24';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, y, paperDimensions.paperWidth, indicatorHeight);

    // Warning text
    ctx.fillStyle = '#d97706';
    ctx.font = `10px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    ctx.fillText(
      '⚠️ Fallback Mode: Backend layout validation failed',
      paperDimensions.paperWidth / 2,
      y + indicatorHeight / 2
    );
    
    ctx.restore();
  }

  /**
   * Log fallback activation with structured information
   */
  static logFallbackActivation(reason: string, validationErrors: string[]): void {
    console.warn('🔄 [ValidationFallback] Activated due to:', {
      reason,
      errors: validationErrors,
      timestamp: new Date().toISOString(),
      action: 'LOCAL_RENDER_UNTIL_VALID_PLAN',
      fallbackFeatures: [
        'Simple word wrapping',
        'Margin guide display',
        'Text truncation handling',
        'Placeholder text support'
      ]
    });
  }

  /**
   * Enhanced fallback with error details
   */
  static renderFallbackWithErrors(
    ctx: CanvasRenderingContext2D,
    text: string,
    paperDimensions: PaperDimensions,
    validationErrors: string[],
    options: FallbackRenderOptions = {}
  ): void {
    // Render normal fallback first
    this.renderFallback(ctx, text, paperDimensions, options);

    // Add error details if in development mode
    if (process.env.NODE_ENV === 'development' && validationErrors.length > 0) {
      this.renderErrorDetails(ctx, paperDimensions, validationErrors);
    }
  }

  /**
   * Render validation error details (development mode only)
   */
  private static renderErrorDetails(
    ctx: CanvasRenderingContext2D,
    paperDimensions: PaperDimensions,
    validationErrors: string[]
  ): void {
    ctx.save();
    
    const errorBoxHeight = Math.min(200, validationErrors.length * 16 + 40);
    const errorBoxY = paperDimensions.paperHeight - errorBoxHeight - 30;
    
    // Semi-transparent error box
    ctx.fillStyle = 'rgba(239, 68, 68, 0.1)';
    ctx.fillRect(10, errorBoxY, paperDimensions.paperWidth - 20, errorBoxHeight);
    
    // Error box border
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 1;
    ctx.strokeRect(10, errorBoxY, paperDimensions.paperWidth - 20, errorBoxHeight);
    
    // Error title
    ctx.fillStyle = '#dc2626';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText('Validation Errors:', 20, errorBoxY + 10);
    
    // Error list
    ctx.font = '10px monospace';
    ctx.fillStyle = '#991b1b';
    
    validationErrors.slice(0, 10).forEach((error, index) => {
      const truncatedError = error.length > 80 ? error.substring(0, 77) + '...' : error;
      ctx.fillText(`• ${truncatedError}`, 20, errorBoxY + 30 + index * 16);
    });
    
    if (validationErrors.length > 10) {
      ctx.fillText(`... and ${validationErrors.length - 10} more errors`, 20, errorBoxY + 30 + 10 * 16);
    }
    
    ctx.restore();
  }

  /**
   * Check if fallback mode should be used based on validation result
   */
  static shouldUseFallback(validationResult: { isValid: boolean; errors: string[] }): boolean {
    return !validationResult.isValid || validationResult.errors.length > 0;
  }

  /**
   * Get fallback rendering metrics for performance monitoring
   */
  static getFallbackMetrics(): {
    fallbackActivations: number;
    lastActivation: string | null;
    commonErrors: string[];
  } {
    // This would be implemented with actual metrics tracking
    return {
      fallbackActivations: 0,
      lastActivation: null,
      commonErrors: []
    };
  }
}
